import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { PaginationResponse } from 'modules/_shared/components/Pagination/Pagination';
import { State } from 'redux/initialState';
import { store } from 'redux/store';

import { getBeneficiaries } from '../api/beneficiaryActions';
import { IncentivesMarketBeneficiary } from '../types';

type UseGetBeneficiariesProps = {
  userId?: string;
  societyId: string | undefined;
  beneficiaryId?: string;
  page?: number;
  items: number;
  sortBy?: string;
  sortOrder?: string;
  searchTerm?: string;
  refreshKey?: number;
};

export const useGetBeneficiaries = ({
  userId,
  societyId,
  beneficiaryId,
  page = 1,
  items,
  searchTerm = '',
  sortBy = 'name',
  sortOrder = 'asc',
  refreshKey = 0,
}: UseGetBeneficiariesProps) => {
  const { isAdmin } = useSelector(
    (state: State) => state?.society?.role || {}
  );

  const [data, setData] = useState<IncentivesMarketBeneficiary[]>([]);
  const [pagination, setPagination] = useState<PaginationResponse>();
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const fetchBeneficiaries = useCallback(async () => {
    if (!societyId) return;

    setIsLoading(true);
    setIsError(false);

    try {
      const params: Record<string, unknown> = {
        page,
        items,
        sortBy,
        sortOrder,
        search: searchTerm,
      };
      if (userId) params.userId = userId;
      if (beneficiaryId) params.beneficiaryId = beneficiaryId;

      const response: any = await store.dispatch(
        getBeneficiaries(societyId, params)
      );

      setData(response?.data || []);
      setPagination(response?.pagination);
    } catch {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, [societyId, page, items, sortBy, sortOrder, searchTerm, userId, beneficiaryId]);

  useEffect(() => {
    fetchBeneficiaries();
  }, [fetchBeneficiaries, refreshKey]);

  return { data, pagination, isLoading, isError, refetch: fetchBeneficiaries };
};
